/**
 * Movimiento DRIKVAR — con GSAP. Tres efectos:
 *
 *   data-drikvar-rotate   en un titular partido con snippets/drikvar-chars:
 *                         sus letras giran una a una hasta ponerse de frente,
 *                         una sola vez, y ahí se quedan.
 *   data-drikvar-magnet   en un botón: se atrae hacia el cursor.
 *   data-drikvar-reveal   en un contenedor: sus piezas suben en cascada la
 *                         primera vez que asoma en pantalla.
 *
 * El giro y el revelado corren en cualquier dispositivo; lo magnético solo
 * con ratón.
 *
 * Atributos opcionales del imán:
 *
 *   data-drikvar-magnet="0.28"    fuerza (proporción del desplazamiento)
 *   data-drikvar-magnet-radius="72"  cuánto se infla la zona más allá del botón, en px
 *   data-drikvar-magnet-max="14"     tope del desplazamiento, en px
 *
 * Tres decisiones que vale la pena explicar:
 *
 * 1. GSAP se carga bajo demanda. Este archivo pesa ~3 KB y va en todas las
 *    páginas; las 72 KB de GSAP solo se descargan si la página tiene de verdad
 *    un botón magnético. Así no hay que acertar de antemano en qué plantillas
 *    está el hero.
 *
 * 2. Un único listener en `document` en vez de uno por botón. Con eso la zona
 *    es un rectángulo alrededor del botón —que es lo que hace el efecto— sin
 *    envolverlo en un contenedor con padding, que cambiaría la maquetación y,
 *    peor, se quedaría con clics destinados a lo que tiene al lado.
 *
 * 3. Las medidas se cachean en coordenadas de página. Si se leyera el rect en
 *    cada frame se estaría midiendo el botón *ya desplazado*, y el efecto se
 *    realimentaría hasta irse solo.
 *
 * `overwrite: true` en ambos tweens: los dos animan x/y sobre el mismo target,
 * así que cada uno debe matar al anterior. Sin eso, entrar y salir rápido deja
 * dos tweens compitiendo y el botón tiembla.
 */

(function () {
  const script = document.currentScript;
  const URL_GSAP = script && script.dataset.gsap;

  const SELECTOR = '[data-drikvar-magnet]';
  const SELECTOR_GIRO = '[data-drikvar-rotate]';
  const SELECTOR_MENU = '[data-drikvar-menu]';
  const SELECTOR_REVELA = '[data-drikvar-reveal]';
  const SELECTOR_CARGA = `${SELECTOR},${SELECTOR_GIRO},${SELECTOR_MENU},${SELECTOR_REVELA}`;
  const FUERZA = 0.28;
  const RADIO = 72;
  const TOPE = 14;

  const puntero_fino = window.matchMedia('(hover: hover) and (pointer: fine)');
  const menos_movimiento = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {Map<Element, {fuerza:number, radio:number, tope:number, cx:number, cy:number, hw:number, hh:number, activo:boolean}>} */
  const imanes = new Map();

  let gsap = null;
  let promesa_gsap = null;
  let escuchando = false;
  let raton = { x: -9999, y: -9999 };
  let frame_pedido = false;

  function cargar_gsap() {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (promesa_gsap) return promesa_gsap;

    promesa_gsap = new Promise((resolver, rechazar) => {
      if (!URL_GSAP) return rechazar(new Error('Falta la URL de GSAP'));
      const etiqueta = document.createElement('script');
      etiqueta.src = URL_GSAP;
      etiqueta.onload = () => resolver(window.gsap);
      etiqueta.onerror = () => rechazar(new Error('No se pudo cargar GSAP'));
      document.head.appendChild(etiqueta);
    });

    return promesa_gsap;
  }

  /**
   * Guarda el centro y la mitad de la zona en coordenadas de página. Se vuelve
   * a cero antes de medir para que el desplazamiento actual no contamine la
   * lectura.
   */
  function medir(el, iman) {
    gsap.set(el, { x: 0, y: 0 });
    iman.activo = false;

    const caja = el.getBoundingClientRect();
    iman.cx = caja.left + window.scrollX + caja.width / 2;
    iman.cy = caja.top + window.scrollY + caja.height / 2;
    iman.hw = caja.width / 2 + iman.radio;
    iman.hh = caja.height / 2 + iman.radio;
  }

  function medir_todos() {
    imanes.forEach((iman, el) => medir(el, iman));
  }

  function registrar(raiz) {
    raiz.querySelectorAll(SELECTOR).forEach((el) => {
      if (imanes.has(el)) return;

      const iman = {
        fuerza: parseFloat(el.dataset.drikvarMagnet) || FUERZA,
        radio: parseFloat(el.dataset.drikvarMagnetRadius) || RADIO,
        tope: parseFloat(el.dataset.drikvarMagnetMax) || TOPE,
        activo: false,
      };

      el.style.willChange = 'transform';
      imanes.set(el, iman);
      medir(el, iman);
    });
  }

  function soltar(el) {
    gsap.to(el, {
      x: 0,
      y: 0,
      duration: 0.7,
      ease: 'elastic.out(1, 0.4)',
      overwrite: true,
    });
  }

  function frame() {
    frame_pedido = false;

    // Si el usuario activa «reducir movimiento» a mitad de sesión, se para.
    if (menos_movimiento.matches) {
      imanes.forEach((iman, el) => {
        if (!iman.activo) return;
        iman.activo = false;
        gsap.set(el, { x: 0, y: 0 });
      });
      return;
    }

    const limitar = gsap.utils.clamp;

    imanes.forEach((iman, el) => {
      const dx = raton.x - iman.cx;
      const dy = raton.y - iman.cy;

      // Un botón deshabilitado no invita a nada: no se mueve. Se comprueba en
      // vivo porque la ficha de producto lo activa y desactiva según la talla.
      const inerte = el.disabled || el.getAttribute('aria-disabled') === 'true';
      const dentro = !inerte && Math.abs(dx) <= iman.hw && Math.abs(dy) <= iman.hh;

      if (dentro) {
        iman.activo = true;
        gsap.to(el, {
          x: limitar(-iman.tope, iman.tope, dx * iman.fuerza),
          y: limitar(-iman.tope, iman.tope, dy * iman.fuerza),
          duration: 0.4,
          ease: 'power2.out',
          overwrite: true,
        });
      } else if (iman.activo) {
        // Solo una vez al salir: si no, el rebote elástico se relanzaría en
        // cada frame y nunca llegaría a asentarse.
        iman.activo = false;
        soltar(el);
      }
    });
  }

  function al_mover(evento) {
    raton = { x: evento.pageX, y: evento.pageY };
    if (frame_pedido) return;
    frame_pedido = true;
    requestAnimationFrame(frame);
  }

  function al_salir() {
    raton = { x: -99999, y: -99999 };
    imanes.forEach((iman, el) => {
      if (!iman.activo) return;
      iman.activo = false;
      soltar(el);
    });
  }

  function escuchar() {
    if (escuchando) return;
    escuchando = true;

    document.addEventListener('pointermove', al_mover, { passive: true });
    document.addEventListener('pointerleave', al_salir);
    window.addEventListener('blur', al_salir);

    let espera;
    window.addEventListener('resize', () => {
      clearTimeout(espera);
      espera = setTimeout(medir_todos, 150);
    });

    // Las medidas dependen del ancho del texto: hasta que no están las fuentes
    // de la marca, el botón mide otra cosa.
    if (document.fonts) document.fonts.ready.then(medir_todos);
    window.addEventListener('load', medir_todos);
  }

  /**
   * Giro de entrada de un titular partido en letras.
   *
   * Cada letra rota sobre un eje horizontal situado *detrás* del texto —eso es
   * lo que hace el `z` del transformOrigin—, así que no gira sobre sí misma:
   * describe un arco, como si el titular estuviera impreso en un tambor que
   * se detiene de frente. Una sola pasada; después se queda quieto.
   */
  function girar(titular) {
    const letras = titular.querySelectorAll('.drikvar-char');
    if (!letras.length || titular.dataset.drikvarRotado) return;
    titular.dataset.drikvarRotado = '1';

    const alto = titular.getBoundingClientRect().height;

    // Ambas medidas salen del tamaño real del titular, que es fluido con el
    // viewport: con valores fijos, el arco se ve violento en móvil y plano en
    // pantallas grandes.
    gsap.set(titular, { perspective: gsap.utils.clamp(700, 1800, alto * 2.2) });
    const fondo = -Math.round(alto * 0.5);

    gsap.fromTo(
      letras,
      { rotationX: -90 },
      {
        rotationX: 0,
        duration: 1,
        ease: 'power4.out',
        stagger: 0.05,
        transformOrigin: `50% 50% ${fondo}px`,
        force3D: true,

        // El estado de partida ya lo lleva GSAP en línea, así que la clase que
        // lo daba por CSS sobra. Hay que quitarla antes del clearProps final:
        // si no, al limpiar los transforms las letras volverían a ponerse de
        // canto y el titular desaparecería.
        onStart: () => titular.classList.remove('drikvar-hero__wordmark--de-canto'),

        // Al terminar se devuelve el texto a su render normal, sin capa 3D:
        // un transform activo lo deja ligeramente borroso.
        onComplete: () => {
          gsap.set(letras, { clearProps: 'transform' });
          gsap.set(titular, { clearProps: 'perspective' });
        },
      }
    );
  }

  /* ── Revelado al entrar en pantalla ──────────────────────────────────────
   *
   * `data-drikvar-reveal` en un contenedor: sus piezas suben y aparecen en
   * cascada la primera vez que asoma. Lo usan el carrito, la búsqueda, el
   * blog y la lista de colecciones, así que vive aquí y no en cada sección.
   *
   * Se marcan las piezas con `data-drikvar-reveal-item`; si no hay ninguna,
   * se animan los hijos directos. El valor del atributo, si se pone, es
   * cuántos píxeles suben.
   *
   * Va con IntersectionObserver y `gsap.from`, no con ScrollTrigger: el
   * plugin es otro archivo que descargar y esto no necesita ni scrub ni
   * pin. Y `from` —no `fromTo`— es deliberado: el estado escondido lo pone
   * el propio GSAP en el momento de animar, así que si el archivo no carga,
   * nunca hubo nada oculto que descubrir. Nada de esto puede ser lo único
   * que hace visible el contenido.
   */

  let ojo = null;

  function revelar(bloque) {
    const marcadas = bloque.querySelectorAll('[data-drikvar-reveal-item]');
    const piezas = marcadas.length ? Array.from(marcadas) : Array.from(bloque.children);
    if (!piezas.length) return;

    const salto = Number(bloque.dataset.drikvarReveal) || 18;

    gsap.from(piezas, {
      y: salto,
      opacity: 0,
      duration: 0.7,
      ease: 'power3.out',

      // El escalonado se reparte dentro de un presupuesto fijo. Con un valor
      // por pieza, una rejilla de treinta productos tardaría casi dos
      // segundos en terminar de aparecer y las últimas llegarían tarde.
      stagger: Math.min(0.06, 0.45 / piezas.length),

      // Un transform activo deja el texto ligeramente borroso, y aquí debajo
      // hay precios que tienen que leerse nítidos.
      clearProps: 'transform,opacity',
      overwrite: true,
    });
  }

  function montar_revelados(raiz) {
    const bloques = raiz.querySelectorAll(SELECTOR_REVELA);
    if (!bloques.length) return;

    if (!ojo) {
      ojo = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            if (!entrada.isIntersecting) return;
            // Una sola vez: al volver a subir no se repite.
            ojo.unobserve(entrada.target);
            revelar(entrada.target);
          });
        },
        // Un pelín de margen inferior para que arranque justo cuando el
        // bloque asoma de verdad, y no cuando roza el borde con un píxel.
        { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
      );
    }

    bloques.forEach((bloque) => {
      if (bloque.dataset.drikvarRevelado) return;
      bloque.dataset.drikvarRevelado = '1';
      ojo.observe(bloque);
    });
  }

  /* ── Menú de tres paneles ────────────────────────────────────────────────
   *
   * Una sola línea de tiempo que se vacía con .clear() y se reconstruye en
   * cada cambio de estado. Es lo que permite que abrir y cerrar sean gestos
   * distintos en vez de la misma animación al revés.
   *
   * Al abrir se usa `fromTo`: cada apertura arranca de un punto conocido, dé
   * igual dónde estuvieran los paneles si el visitante interrumpió el cierre.
   * Al cerrar se usa `to`: la caída empieza donde esté cada cosa en ese
   * momento, y así no da el tirón de recolocarse antes de irse.
   */

  /** @type {Map<Element, {abierto:boolean, tl:any, toggle:Element}>} */
  const menus = new Map();

  function abrir_menu(overlay, estado) {
    const paneles = overlay.querySelectorAll('.drikvar-menu__panel');
    const items = overlay.querySelectorAll('.drikvar-menu__item');

    estado.tl = estado.tl || gsap.timeline();
    estado.tl.clear();
    estado.tl
      .set(overlay, { visibility: 'visible', pointerEvents: 'auto' })
      .fromTo(overlay.querySelector('.drikvar-menu__scrim'), { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0)
      .fromTo(paneles, { x: '101%', y: 0, rotation: 0 }, { x: '0%', duration: 0.6, ease: 'back.out(1.1)', stagger: 0.12 }, 0)
      .fromTo(items, { opacity: 0, x: -20 }, { opacity: 1, x: 0, duration: 1.1, ease: 'expo.out', stagger: 0.03 }, 0.1)

      // La X se dibuja girando un cuarto de vuelta. Entra la última, cuando
      // el panel ya está puesto: es la salida, no lo primero que hay que ver.
      .fromTo(
        estado.cerrar,
        { opacity: 0, rotate: -90, scale: 0.6 },
        { opacity: 1, rotate: 0, scale: 1, duration: 0.4, ease: 'back.out(1.6)' },
        0.35
      );

    // La línea de tiempo se reutiliza, así que al reconstruirla su cabeza de
    // lectura sigue donde acabó la anterior: más allá del final del contenido
    // nuevo. Sin devolverla a cero, la animación se da por terminada nada más
    // empezar y el menú aparece o desaparece de golpe.
    estado.tl.play(0);
  }

  function cerrar_menu(overlay, estado) {
    const paneles = overlay.querySelectorAll('.drikvar-menu__panel');

    estado.tl = estado.tl || gsap.timeline();
    estado.tl.clear();
    estado.tl
      .to(estado.cerrar, { opacity: 0, scale: 0.7, duration: 0.18, ease: 'power2.in' }, 0)

      // Los paneles se van cayendo, el de abajo primero, cada uno con su
      // propio ladeo. El azar es lo que evita que parezca una persiana.
      //
      // La caída va con `power2.in` y no con `power3.in`: cuanto más brusca
      // la curva, más se concentra el recorrido en el último suspiro y menos
      // se ve caer nada. Y el recorrido se queda en 130vh, lo justo para
      // salir de pantalla, porque cada viewport de más hay que recorrerlo en
      // el mismo tiempo.
      .to(
        paneles,
        {
          y: '130vh',
          rotation: 'random(-12, 12)',
          duration: 1.15,
          ease: 'power2.in',
          stagger: { from: 'end', each: 0.09 },
        },
        0
      )

      // El velo se va al final. Si se desvanece antes, la caída ocurre sobre
      // la página ya visible y se pierde.
      .to(overlay.querySelector('.drikvar-menu__scrim'), { opacity: 0, duration: 0.45, ease: 'power2.in' }, '>-0.5')
      .call(() => {
        // Primero se quita el estado abierto y después se limpian los estilos
        // en línea. Al revés, el CSS del estado abierto dejaría los paneles a
        // la vista justo en el momento en que GSAP suelta el control.
        overlay.removeAttribute('data-open');
        gsap.set(paneles, { clearProps: 'transform,opacity' });
        gsap.set(overlay, { clearProps: 'visibility,pointerEvents' });
      });

    estado.tl.play(0);
  }

  function alternar_menu(overlay, forzar_cierre) {
    const estado = menus.get(overlay);
    if (!estado) return;

    const abriendo = forzar_cierre === true ? false : !estado.abierto;
    if (abriendo === estado.abierto) return;
    estado.abierto = abriendo;

    estado.toggle.setAttribute('aria-expanded', String(abriendo));

    // `inert` saca del recorrido de tabulación y del árbol de accesibilidad
    // todo el subárbol de una vez. Hace lo mismo que ir poniendo tabindex a
    // mano en cada enlace, pero sin poder olvidarse de ninguno.
    //
    // Se aplica en los dos sentidos: al abrir se inertiza la página, que se
    // ha quedado debajo del velo. Si no, tabulando se llega a los enlaces de
    // la cabecera —invisibles pero ahí— y el foco se pierde detrás del menú.
    overlay.inert = !abriendo;
    const pagina = document.querySelector('.page-wrapper');
    if (pagina) pagina.inert = abriendo;
    document.body.classList.toggle('drikvar-menu-abierto', abriendo);

    if (abriendo) overlay.setAttribute('data-open', '');

    // El foco sigue al menú: entra en la X al abrir y vuelve al botón de la
    // barra al cerrar. Sin esto, al abrir el foco se quedaría en un botón que
    // ha quedado debajo del velo.
    if (abriendo) estado.cerrar?.focus();
    else estado.toggle.focus();

    if (!gsap || menos_movimiento.matches) {
      // Sin animación: el atributo por sí solo abre y cierra.
      if (!abriendo) overlay.removeAttribute('data-open');
      return;
    }

    if (abriendo) abrir_menu(overlay, estado);
    else cerrar_menu(overlay, estado);
  }

  function montar_menus(raiz) {
    raiz.querySelectorAll('[data-drikvar-menu]').forEach((overlay) => {
      if (menus.has(overlay)) return;

      const toggle = document.querySelector(`[aria-controls="${overlay.id}"]`);
      if (!toggle) return;

      // En el editor de temas la cabecera se vuelve a pintar sin recargar. El
      // overlay de la vez anterior ya está colgado de <body> y hay que
      // retirarlo, o se irían acumulando uno encima de otro.
      menus.forEach((_, anterior) => {
        if (anterior !== overlay && anterior.id === overlay.id) {
          anterior.remove();
          menus.delete(anterior);
        }
      });

      // El overlay se muda a <body>. Va en posición fija y, colgando del
      // header, basta un `transform` en cualquier ancestro para convertirlo
      // en su bloque contenedor: el menú se recortaría dentro de la barra.
      document.body.appendChild(overlay);
      overlay.inert = true;

      const estado = {
        abierto: false,
        toggle,
        tl: null,
        cerrar: overlay.querySelector('[data-drikvar-menu-close]'),
      };
      menus.set(overlay, estado);

      toggle.addEventListener('click', () => alternar_menu(overlay));
      estado.cerrar?.addEventListener('click', () => alternar_menu(overlay, true));
      overlay.querySelector('.drikvar-menu__scrim')?.addEventListener('click', () => alternar_menu(overlay, true));

      // Al navegar, el menú debe quedar cerrado: si el enlace apunta a un
      // ancla de la misma página no hay recarga que lo cierre sola.
      overlay.querySelectorAll('a[href]').forEach((enlace) => {
        enlace.addEventListener('click', () => alternar_menu(overlay, true));
      });

      document.addEventListener('keydown', (evento) => {
        if (evento.key !== 'Escape' || !estado.abierto) return;
        alternar_menu(overlay, true);
        toggle.focus();
      });

      // Si se pasa a escritorio con el menú abierto, el botón desaparece y el
      // visitante se quedaría sin forma de cerrarlo.
      window.addEventListener('resize', () => {
        if (!estado.abierto || !toggle.checkVisibility) return;
        if (!toggle.checkVisibility()) alternar_menu(overlay, true);
      });
    });
  }

  /* ── Modal de newsletter ─────────────────────────────────────────────────
   *
   * <dialog> nativo: la trampa de foco, Escape y la inertización del resto de
   * la página las hace el navegador. Aquí solo se decide *cuándo* se abre.
   *
   * Se abre por lo que ocurra antes: el temporizador, o el amago de salir
   * —el cursor cruzando el borde superior de la ventana, que en escritorio es
   * el gesto de irse a la barra de direcciones o a cerrar la pestaña—. En
   * táctil no hay amago posible, así que ahí solo cuenta el tiempo.
   */

  const CLAVE_NEWS = 'drikvar:news';

  function silenciado() {
    try {
      const hasta = Number(localStorage.getItem(CLAVE_NEWS));
      return Boolean(hasta) && Date.now() < hasta;
    } catch {
      // Modo privado o almacenamiento bloqueado: mejor no insistir.
      return true;
    }
  }

  function silenciar(dias) {
    try {
      localStorage.setItem(CLAVE_NEWS, String(Date.now() + dias * 864e5));
    } catch {
      /* sin almacenamiento no se puede recordar; se acepta */
    }
  }

  function montar_newsletter(raiz) {
    const modal = raiz.querySelector('[data-drikvar-news]');
    if (!modal || modal.dataset.drikvarMontado) return;
    modal.dataset.drikvarMontado = '1';

    const dias = Number(modal.dataset.days) || 30;
    const espera = (Number(modal.dataset.delay) || 0) * 1000;
    let temporizador;

    const abrir = () => {
      if (modal.open) return;

      // Con el menú abierto la página está inertizada, y este <dialog> cuelga
      // de ella: se abriría inservible. Se espera a que el menú se cierre.
      if (document.body.classList.contains('drikvar-menu-abierto')) {
        clearTimeout(temporizador);
        temporizador = setTimeout(abrir, 5000);
        return;
      }

      clearTimeout(temporizador);
      document.removeEventListener('mouseout', al_amagar);
      modal.showModal();

      if (!gsap || menos_movimiento.matches) return;
      gsap.fromTo(
        modal,
        { y: 24, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.45, ease: 'back.out(1.2)', clearProps: 'transform,opacity' }
      );
    };

    const cerrar = () => {
      silenciar(dias);
      modal.close();
    };

    function al_amagar(evento) {
      // `relatedTarget` vacío al salir por arriba: el puntero abandonó la
      // ventana de verdad, no saltó de un elemento a otro.
      if (evento.relatedTarget || evento.clientY > 8) return;
      abrir();
    }

    modal.querySelector('[data-drikvar-news-close]')?.addEventListener('click', cerrar);

    // Cerrar por Escape o por clic en el fondo lo hace el navegador, pero
    // entonces no pasa por `cerrar` y no quedaría registrado el silencio.
    modal.addEventListener('close', () => silenciar(dias));
    modal.addEventListener('click', (evento) => {
      if (evento.target === modal) cerrar();
    });

    // Cualquier enlace de la tienda puede abrirlo a mano; eso no cuenta como
    // aparición automática y no debe quedar silenciado de antemano.
    document.querySelectorAll('[data-drikvar-news-open]').forEach((disparador) => {
      disparador.addEventListener('click', (evento) => {
        evento.preventDefault();
        abrir();
      });
    });

    // Vuelve del envío: se muestra el acuse y ya no se vuelve a pedir. La
    // marca la deja el propio formulario, porque `form.posted_successfully?`
    // solo existe dentro de {% form %} y no se puede leer desde el <dialog>.
    if (modal.querySelector('[data-drikvar-news-posted]')) {
      silenciar(dias);
      abrir();
      return;
    }

    if (silenciado()) return;

    // En el editor, al seleccionar la sección se abre para poder verla.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (evento) => {
        if (evento.target.contains(modal)) abrir();
      });
      return;
    }

    temporizador = setTimeout(abrir, espera);
    if (puntero_fino.matches) document.addEventListener('mouseout', al_amagar);
  }

  function iniciar(raiz) {
    // El menú se cablea siempre, incluso sin GSAP y con «reducir movimiento»
    // activado: es navegación, no adorno. Sin él, en móvil no se puede ir a
    // ningún sitio.
    montar_menus(raiz);
    montar_newsletter(raiz);

    if (menos_movimiento.matches) return;
    if (!raiz.querySelector(SELECTOR_CARGA)) return;

    cargar_gsap()
      .then((lib) => {
        gsap = lib;

        raiz.querySelectorAll(SELECTOR_GIRO).forEach(girar);
        montar_revelados(raiz);

        // Lo magnético pide ratón; el giro de entrada no.
        if (!puntero_fino.matches) return;
        registrar(raiz);
        escuchar();
      })
      .catch(() => {
        // Sin GSAP los botones siguen siendo botones y el titular se ve
        // quieto: la red de seguridad de la sección le quita la clase.
      });
  }

  iniciar(document);

  /**
   * Superficie mínima para el resto de la marca. La usa el carrito, que
   * rehace su lista sin recargar: al cambiar de sitio las filas, las medidas
   * que el imán tenía guardadas del botón de pagar dejan de valer.
   *
   * Ambas funciones no hacen nada si GSAP no llegó a cargar, así que quien
   * llame no tiene que comprobarlo.
   */
  window.DrikvarMotion = {
    medir: () => {
      if (gsap) medir_todos();
    },
    revelar: (raiz) => {
      if (gsap) montar_revelados(raiz || document);
    },
  };

  // El editor de temas monta y desmonta secciones sin recargar.
  document.addEventListener('shopify:section:load', (evento) => {
    iniciar(evento.target);
    if (gsap) medir_todos();
  });

  document.addEventListener('shopify:section:unload', (evento) => {
    imanes.forEach((_, el) => {
      if (evento.target.contains(el)) imanes.delete(el);
    });
  });
})();
